import React, { useContext } from 'react';
import { checkConditional } from '../../../../../Helpers';
import { Field, FieldValueType, SectionValueType } from '../../../../../Models';
import { FieldInput } from '../../FieldInput/FieldInput';
import { StateMandatedReportContext } from '../../../Main';
import { ItemMissingInformationContext } from '../../ContactFormView/ContactFormView';

import { useNeedHelpTarget } from '../../ContactFormView/Contexts/NeedHelpContext';

export interface SectionFormFieldProps {
  disabled?: boolean;
  disabledMessage?: string;
  field: Field;
  sectionValue: SectionValueType;
  parentPath?: string;
  onChange: (field: Field, value: FieldValueType) => void;
}

export const SectionFormField = ({
  disabled,
  disabledMessage,
  field,
  sectionValue,
  parentPath,
  onChange
}: SectionFormFieldProps): React.ReactElement<SectionFormFieldProps> => {
  const showStateMandated: boolean = useContext(StateMandatedReportContext);
  const showMissingInformation: boolean = useContext(
    ItemMissingInformationContext
  );
  const { needHelpTargetPath, setNeedHelpTargetPath } = useNeedHelpTarget();

  const dataPath = parentPath ? `${parentPath}.${field.name}` : field.name;

  const renderConditionalInputFields = (field: Field) => {
    let fieldsToAdd: Field[] = [];

    if (field.condition_if) {
      let conditionMet = false;
      for (let conditional of field.condition_if) {
        if (
          checkConditional(
            'conditional_if',
            conditional,
            sectionValue[field.name]
          )
        ) {
          if (!conditionMet) conditionMet = true;
          fieldsToAdd = [...fieldsToAdd, ...conditional.fields];
        }
      }

      if (field.condition_else && !conditionMet) {
        fieldsToAdd = [...fieldsToAdd, ...field.condition_else];
      }
    }

    if (field.condition_if_not) {
      for (let conditional of field.condition_if_not) {
        if (
          checkConditional(
            'conditional_if_not',
            conditional,
            sectionValue[field.name]
          )
        ) {
          fieldsToAdd = [...fieldsToAdd, ...conditional.fields];
        }
      }
    }

    //Remove deprecated fields
    fieldsToAdd = fieldsToAdd.filter((field: Field) => !field.deprecated);

    return fieldsToAdd.map((field: Field) => (
      <SectionFormField
        key={field.name}
        field={field}
        sectionValue={sectionValue}
        parentPath={parentPath}
        onChange={onChange}
      />
    ));
  };

  return (
    <React.Fragment>
      <div data-field-path={dataPath}>
        <FieldInput
          disabled={disabled}
          disabledMessage={disabledMessage}
          key={field.name}
          field={field}
          value={sectionValue[field.name]}
          showStateMandated={showStateMandated}
          showMissingInformation={showMissingInformation}
          needHelpTargetPath={needHelpTargetPath}
          setNeedHelpTargetPath={setNeedHelpTargetPath}
          onChange={(value: FieldValueType) => onChange(field, value)}
        />
      </div>
      {(field.condition_if || field.condition_if_not || field.condition_else) &&
        renderConditionalInputFields(field)}
    </React.Fragment>
  );
};
